import { createContext, useContext, type ReactNode } from 'react'
import type { CdnSource } from '../crm/client'
import type { JobDetail } from '../bulkMedia/types'

/**
 * What the Library pages need from the outside world, bound to one account:
 * OnlyFans through the CRM gateway, and OnlyFans media for thumbnails.
 * The console provides the real CRM client; tests provide fakes.
 */
export interface LibraryDeps {
  gateway: <T = unknown>(path: string, body?: Record<string, unknown>) => Promise<T>
  /** `signal`: the tile left before its turn came, so its queued fetch can be dropped. */
  loadMedia: (url: string, signal?: AbortSignal) => Promise<CdnSource>
  /** A playable URL for OnlyFans video/audio (streamed through the CRM). */
  streamMedia?: (url: string) => Promise<string>
  /** Operator notes on Vault items (console DB, of_content.operator_notes). */
  notes?: VaultNotesApi
  /** The Account's Badges (console DB, vault_badges); which items carry them comes with `notes.load`. */
  badges?: VaultBadgesApi
  /** Uploads into the Vault (a one-Account Bulk Media job, ADR 0025); absent = no Upload button. */
  upload?: VaultUploadApi
}

export interface VaultUploadApi {
  /** Send one file to the console; resolves with its staged file id. */
  stage: (file: File, onProgress: (fraction: number) => void, signal: AbortSignal) => Promise<string>
  /** Start the job for this Account: the staged files into the named Category, or none. */
  start: (fileIds: string[], category: string | null) => Promise<JobDetail>
  job: (jobId: string) => Promise<JobDetail>
  /** Retry the job's failed items. */
  retry: (jobId: string) => Promise<JobDetail>
}

export interface VaultAnnotations {
  /** Ids without notes are absent. */
  notes: Record<string, string>
  /** The Badge ids each item carries; ids without badges are absent. */
  badges?: Record<string, number[]>
}

export interface VaultNotesApi {
  /** Notes (and badges) for these item ids, in one call. */
  load: (ids: string[]) => Promise<VaultAnnotations>
  /** Save one item's notes ('' clears); resolves with what was stored. */
  save: (id: string, text: string) => Promise<string>
}

export type BadgeColor = 'red' | 'orange' | 'amber' | 'green' | 'teal' | 'blue' | 'indigo' | 'purple' | 'pink' | 'grey'

export interface Badge {
  id: number
  name: string
  color: BadgeColor
  /** How many items carry it (as of the last load or change). */
  count: number
}

export interface VaultBadgesApi {
  list: () => Promise<Badge[]>
  create: (name: string, color: BadgeColor) => Promise<Badge>
  update: (id: number, patch: { name?: string; color?: BadgeColor }) => Promise<Badge>
  remove: (id: number) => Promise<void>
  /** Attach to `add`, detach from `remove`; resolves with the badge's new count. */
  setItems: (id: number, add: string[], remove: string[]) => Promise<Badge>
}

const Ctx = createContext<LibraryDeps | null>(null)

export function LibraryDepsProvider({ value, children }: { value: LibraryDeps; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useLibraryDeps(): LibraryDeps {
  const deps = useContext(Ctx)
  if (!deps) throw new Error('useLibraryDeps outside LibraryDepsProvider')
  return deps
}
